import * as Sentry from "@sentry/react";
import React from "react";
import ReactDOM from "react-dom/client";
import "bootstrap/dist/css/bootstrap.min.css";
import "./index.css";
import App from "./App";
import RootErrorBoundary from "./components/errorBoundary/RootErrorBoundary";
import {
  makeSentryBeforeBreadcrumb,
  makeSentryBeforeSend,
  scrubEventUrls,
  scrubRecordingEvent,
} from "./config/sentryFilters";
// import reportWebVitals from "./reportWebVitals";

// GMA-1178: prefer the explicit REACT_APP_ENVIRONMENT tag (set in
// shared/.env on each server) so Sentry groups events by deploy
// environment even if hostname logic misfires. Fall back to the
// legacy REACT_APP_SENTRY_ENVIRONMENT variable and the hostname
// heuristic for backwards compatibility.
const resolveEnvironment = () => {
  if (process.env.REACT_APP_ENVIRONMENT) return process.env.REACT_APP_ENVIRONMENT;
  if (process.env.REACT_APP_SENTRY_ENVIRONMENT) return process.env.REACT_APP_SENTRY_ENVIRONMENT;
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host.includes("stg")) return "staging";
    if (host === "localhost") return "development";
  }
  return "production";
};

// GMA-1178: bind the Sentry release to the deployed commit SHA so
// ignoreErrors / release-scoped filters track the actual artifact.
const resolveRelease = () => {
  const sha =
    process.env.REACT_APP_COMMIT_SHA || process.env.REACT_APP_GIT_SHA;
  if (sha) return `compasscare-frontend@${sha}`;
  const version = process.env.REACT_APP_VERSION || "1.0.0";
  return `compasscare-frontend@${version}`;
};

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: resolveEnvironment(),
  release: resolveRelease(),
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      // The rrweb recording payload bypasses event processors — the Meta
      // event's data.href carries the full page URL, invite code included.
      beforeAddRecordingEvent: (e) => scrubRecordingEvent(e),
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: process.env.NODE_ENV === "production" ? 0.1 : 1.0,
  // Distributed tracing - connect frontend errors to backend
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/prod-api\.compasscare\.app/,
    /^https:\/\/staging-api\.compasscare\.app/,
    /^https:\/\/stg2-api\.compasscare\.app/,
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  // GMA-1177 / #589: scanner, AbortError and localhost drops — plus
  // route-scoped fetch-noise suppression — live in config/sentryFilters.js
  // so the policy is tested. Fetch failures on /register and
  // /users/confirm_account ALWAYS report (the 2026-08-26 incident produced
  // zero telemetry because ignoreErrors listed WebKit's "Load failed").
  beforeSend: makeSentryBeforeSend(),
  // #528: presence state (online, platform, call status) is console-logged on
  // every profile view, and the default Breadcrumbs integration turns each
  // console call into a breadcrumb that ships with the NEXT event. beforeSend
  // is too late — it only sees breadcrumbs that survived to an event.
  beforeBreadcrumb: makeSentryBeforeBreadcrumb(),
  // Transactions bypass beforeSend; their fetch/navigation spans carry the
  // same credential-bearing URLs (review finding).
  beforeSendTransaction: (event) => scrubEventUrls(event),
  // Ignore common non-actionable errors
  ignoreErrors: [
    "top.GLOBALS",
    "chrome-extension://",
    "moz-extension://",
    "ResizeObserver loop",
    "Non-Error promise rejection",
    "Script error",
    "ChunkLoadError",
    "canceled",
    "AbortError",
  ],
});

// Replay events bypass beforeSend/beforeSendTransaction but DO run global
// event processors (v8 prepareEvent), and they carry a bare urls[] array —
// one processor covers errors, transactions and replays alike.
Sentry.addEventProcessor(scrubEventUrls);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    {/* #647 — the LAST line between a throw and a white document. Panel
        boundaries (#634) contain profile-panel failures; this catches what
        escapes them all: Layout, page components and their hooks, and every
        non-profile route. */}
    <RootErrorBoundary>
      <App />
    </RootErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
